import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SourceBadge } from '@/components/provenance-badge';
import { IconRail } from '@/features/reader/IconRail';
import { ApiError } from '@/lib/http';
import { fetchLanguages, languageLabel, type Language } from '@/lib/languages';
import type { PassageReading } from '@/features/reader/types';
import { Icon } from '@/features/admin/uploadUi';
import {
  enterTranslation,
  fetchAlignment,
  fetchEditorReading,
  publishTranslation,
} from './editorApi';
import { splitParagraphs, type Provenance } from './editorTypes';
import { PasteSheet } from './PasteSheet';

/**
 * Enter a translation — Editor Workflow, screen 1.
 *
 * The screen that turns an extracted khutbah into something a reader can
 * read. One khutbah at a time, side by side: the Arabic as extracted on one
 * side, a field per passage on the other.
 *
 * Three rules from the design notes that are correctness, not decoration:
 *
 * - **Qur'an passages are rows but never fields.** The server's alignment
 *   excludes them, so the counter says *N of 16*, not 18. A reader is shown
 *   the publisher's translation of meaning instead; inventing one here is the
 *   thing this product forbids outright.
 * - **Provenance is a header-level choice, required before publishing.** A
 *   paste is one act with one origin, and a model-produced translation must
 *   name its model — a reader is told which one.
 * - **Publishing never produces "reviewed".** It produces *unreviewed*, which
 *   lands in the review queue. Entering a translation is not approving it.
 */

const PASSAGE_TYPE_LABELS: Record<string, string> = {
  opening: 'Opening',
  quran: 'Qur’an',
  hadith: 'Hadith',
  prose: 'Body',
  poetry: 'Body',
  heading: 'Body',
  supplication: 'Dua',
  closing: 'Closing',
};

/** The translation badge the chosen provenance will produce. Never "reviewed". */
function PendingTranslationBadge({ provenance }: { provenance: Provenance | null }) {
  const [tone, label] = provenance === null
    ? ['bg-status-stale-bg text-status-stale border border-dashed border-current', 'No translation yet']
    : provenance === 'human'
      ? ['bg-status-user-bg text-status-user', 'Human translation · unreviewed']
      // The word is part of the label, never shortened.
      : ['bg-status-ai-bg text-status-ai', 'AI translation · unreviewed'];
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-sm px-2 text-[12px] font-semibold leading-none',
        tone,
      )}
    >
      <Icon name={provenance === 'ai' ? 'sparkle' : provenance === 'human' ? 'info' : 'clock'} size={13} />
      {label}
    </span>
  );
}

interface Row {
  passage: PassageReading;
  /** Null for Qur'an: shown, never a field. */
  targetIndex: number | null;
}

export function TranslationEditor({ khutbahId }: { khutbahId: string }) {
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState('ml');
  const [provenance, setProvenance] = useState<Provenance | null>(null);
  const [declaredModel, setDeclaredModel] = useState('');
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [pasteOpen, setPasteOpen] = useState(false);
  const [entered, setEntered] = useState<string | null>(null);
  const [published, setPublished] = useState<string | null>(null);
  const fieldRefs = useRef(new Map<string, HTMLTextAreaElement>());

  // The catalogue, not a constant: English became a translation target by a
  // row update, and a hardcoded list here would have needed a deploy to
  // offer it. Cached hard — it changes about once a year.
  const languages = useQuery({
    queryKey: ['languages', 'target'],
    queryFn: ({ signal }) => fetchLanguages('target', signal),
    staleTime: 60 * 60 * 1000,
  });

  const alignment = useQuery({
    // The khutbah is in the key because switching khutbah must not show the
    // previous one's passages while the new ones load.
    queryKey: ['editor', 'alignment', khutbahId],
    queryFn: ({ signal }) => fetchAlignment(khutbahId, signal),
  });

  const reading = useQuery({
    queryKey: ['editor', 'reading', khutbahId, 'ar'],
    queryFn: ({ signal }) => fetchEditorReading(khutbahId, 'ar', signal),
  });

  // Derived during render, not mirrored into state with an Effect: it is a
  // pure function of two queries, and keeping a copy in step is the mistake
  // the React rules call out by name.
  const rows: Row[] = useMemo(() => {
    const passages = reading.data?.passages ?? [];
    const order = new Map(
      (alignment.data ?? []).map((target, index) => [target.passageId, index]),
    );
    return passages.map((passage) => ({
      passage,
      targetIndex: order.get(passage.passageId) ?? null,
    }));
  }, [reading.data, alignment.data]);

  const targets = alignment.data ?? [];
  const chosen: Language | undefined = (languages.data ?? []).find(
    (entry) => entry.code === language,
  );
  const translatable = targets.length;
  const filled = targets.filter((t) => (drafts[t.passageId] ?? '').trim().length > 0).length;
  const complete = translatable > 0 && filled === translatable;
  const provenanceReady = provenance === 'human' || (provenance === 'ai' && declaredModel.trim() !== '');

  const save = useMutation({
    retry: false,
    mutationFn: async () => {
      if (provenance === null) {
        throw new Error('Choose who produced this translation first.');
      }
      // In the server's order, which is the alignment's order — never the
      // order the fields happen to be rendered in.
      const paragraphs = targets.map((t) => (drafts[t.passageId] ?? '').trim());
      return enterTranslation(
        khutbahId,
        language,
        provenance,
        provenance === 'ai' ? declaredModel.trim() : null,
        paragraphs,
        new AbortController().signal,
      );
    },
    onSuccess: (result) => {
      setEntered(result.note);
      setPublished(null);
      void queryClient.invalidateQueries({ queryKey: ['editor', 'reading', khutbahId] });
    },
  });

  const publish = useMutation({
    retry: false,
    mutationFn: () => publishTranslation(khutbahId, language, new AbortController().signal),
    onSuccess: (result) => {
      setPublished(result.note);
      void queryClient.invalidateQueries({ queryKey: ['editor'] });
    },
  });

  // ⌘S saves, ⌘⏎ publishes — the shortcuts the header advertises, so they
  // have to actually work.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key === 's') {
        event.preventDefault();
        if (complete && provenanceReady && !save.isPending) save.mutate();
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (entered !== null && !publish.isPending) publish.mutate();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [complete, provenanceReady, entered, save, publish]);

  /** ↑↓ between fields, as the header says. */
  function moveFocus(from: string, delta: number) {
    const index = targets.findIndex((t) => t.passageId === from);
    const next = targets[index + delta];
    if (next) {
      fieldRefs.current.get(next.passageId)?.focus();
    }
  }

  function applyPaste(text: string) {
    const paragraphs = splitParagraphs(text);
    const next: Record<string, string> = {};
    targets.forEach((target, index) => {
      next[target.passageId] = paragraphs[index] ?? '';
    });
    setDrafts(next);
    setPasteOpen(false);
  }

  const loadError = alignment.error ?? reading.error;

  return (
    <div className="flex min-h-full bg-background text-foreground">
      <IconRail current="admin" />

      <div className="flex min-w-0 flex-1 flex-col">
        <Header
          khutbahId={khutbahId}
          titleAr={reading.data?.titleOriginal ?? null}
          institutionKey={reading.data?.institutionKey ?? ''}
          language={language}
          languages={languages.data ?? []}
          onLanguage={(next) => {
            setLanguage(next);
            // The drafts belong to the language they were typed in. Carrying
            // Malayalam text into the Urdu column would be a silent error
            // nobody notices until it is published.
            setDrafts({});
            setEntered(null);
            setPublished(null);
          }}
          provenance={provenance}
          onProvenance={setProvenance}
          declaredModel={declaredModel}
          onDeclaredModel={setDeclaredModel}
          filled={filled}
          translatable={translatable}
          loading={alignment.isPending || reading.isPending}
          onPaste={() => setPasteOpen(true)}
        />

        {/* `pb-44` is gone: it reserved 176px for an action bar that was
            fixed over the content and is now `sticky bottom-0` outside this
            element, taking its own space. The reservation stayed behind and
            became a screenful of nothing between the last field and the
            bar. */}
        <main className="flex-1 overflow-auto px-4 pb-8 pt-6 sm:px-6">
          <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
            {loadError ? <LoadError error={loadError} /> : null}

            {alignment.isPending || reading.isPending ? (
              <LoadingRows />
            ) : (
              rows.map((row) => (
                <PassageRow
                  key={row.passage.passageId}
                  row={row}
                  language={language}
                  script={chosen?.scriptClass ?? 'script-latin'}
                  direction={chosen?.direction ?? 'ltr'}
                  value={drafts[row.passage.passageId] ?? ''}
                  onChange={(text) =>
                    setDrafts((current) => ({
                      ...current,
                      [row.passage.passageId]: text,
                    }))
                  }
                  onMove={(delta) => moveFocus(row.passage.passageId, delta)}
                  registerRef={(element) => {
                    if (element) {
                      fieldRefs.current.set(row.passage.passageId, element);
                    } else {
                      fieldRefs.current.delete(row.passage.passageId);
                    }
                  }}
                />
              ))
            )}

            {rows.length === 0 && !alignment.isPending && !reading.isPending && !loadError ? (
              <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
                This khutbah has no passages yet. Upload and extract it first.
              </p>
            ) : null}
          </div>
        </main>

        <Footer
          entered={entered}
          published={published}
          complete={complete}
          provenanceReady={provenanceReady}
          filled={filled}
          translatable={translatable}
          saveError={save.error}
          publishError={publish.error}
          saving={save.isPending}
          publishing={publish.isPending}
          onSave={() => save.mutate()}
          onPublish={() => publish.mutate()}
        />
      </div>

      {pasteOpen ? (
        <PasteSheet
          expected={translatable}
          onApply={applyPaste}
          onClose={() => setPasteOpen(false)}
        />
      ) : null}
    </div>
  );
}

function Header({
  khutbahId,
  titleAr,
  institutionKey,
  language,
  languages,
  onLanguage,
  provenance,
  onProvenance,
  declaredModel,
  onDeclaredModel,
  filled,
  translatable,
  loading,
  onPaste,
}: {
  khutbahId: string;
  titleAr: string | null;
  institutionKey: string;
  language: string;
  languages: readonly Language[];
  onLanguage: (next: string) => void;
  provenance: Provenance | null;
  onProvenance: (next: Provenance) => void;
  declaredModel: string;
  onDeclaredModel: (next: string) => void;
  filled: number;
  translatable: number;
  loading: boolean;
  onPaste: () => void;
}) {
  const percent = translatable === 0 ? 0 : Math.round((filled / translatable) * 100);

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-card/95 backdrop-blur">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-3 px-4 py-3 sm:px-6">
        <nav aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li>
              <a href="?screen=upload" className="hover:underline">
                Admin review
              </a>
            </li>
            <li aria-hidden>›</li>
            <li className="font-medium text-foreground" aria-current="page">
              Enter a translation
            </li>
          </ol>
        </nav>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {titleAr ? (
              <h1 lang="ar" dir="rtl" className="script-ar text-xl font-normal">
                {titleAr}
              </h1>
            ) : (
              <h1 className="text-xl font-semibold">Enter a translation</h1>
            )}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {/* Both badges, always: where the Arabic came from, and what
                  this translation will be. Source verification never implies
                  translation review. */}
              <SourceBadge institutionKey={institutionKey} />
              <PendingTranslationBadge provenance={provenance} />
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Target language
              </span>
              <select
                value={language}
                onChange={(event) => onLanguage(event.target.value)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                {languages.length === 0 ? (
                  <option value={language}>Loading…</option>
                ) : (
                  languages.map((entry) => (
                    <option key={entry.code} value={entry.code}>
                      {languageLabel(entry)}
                    </option>
                  ))
                )}
              </select>
            </label>

            <Button type="button" variant="outline" size="lg" onClick={onPaste}>
              <Icon name="upload" size={16} />
              Paste whole translation
            </Button>
          </div>
        </div>

        <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {/* The visible label IS the legend. An sr-only legend beside it
              made a screen reader announce "Provenance" twice. */}
          <legend className="float-start me-5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            Provenance
          </legend>
          {([
            ['human', 'I wrote this myself'],
            ['ai', 'A model produced this'],
          ] as const).map(([value, label]) => (
            <label key={value} className="flex items-center gap-1.5 text-sm">
              <input
                type="radio"
                name="provenance"
                value={value}
                checked={provenance === value}
                onChange={() => onProvenance(value)}
              />
              {label}
            </label>
          ))}

          {provenance === 'ai' ? (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Which model</span>
              <input
                value={declaredModel}
                onChange={(event) => onDeclaredModel(event.target.value)}
                placeholder="e.g. claude-opus-5"
                // Required, not optional: a reader is told which model
                // produced a translation, so it cannot be filled in later.
                required
                aria-invalid={declaredModel.trim() === ''}
                className="h-9 w-56 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-[invalid=true]:border-destructive"
              />
            </label>
          ) : null}

          {provenance === null ? (
            <span className="text-xs text-muted-foreground">
              Required before you publish
            </span>
          ) : null}
        </fieldset>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex min-w-[220px] flex-1 items-center gap-3">
            <span className="text-sm tabular-nums" aria-live="polite">
              {loading
                ? 'Loading passages…'
                : `${filled} of ${translatable} translated`}
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-progress-track">
              <div
                className="h-full rounded-full bg-progress"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
          <span className="text-xs text-muted-foreground">
            ↑↓ passages · ⌘S save · ⌘⏎ publish
          </span>
          <a
            href={`?khutbah=${encodeURIComponent(khutbahId)}`}
            className="text-xs text-primary underline"
          >
            Open the reader
          </a>
        </div>
      </div>
    </header>
  );
}

function LoadingRows() {
  return (
    <div className="flex flex-col gap-4" aria-busy>
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ))}
    </div>
  );
}

function LoadError({ error }: { error: unknown }) {
  const api = error instanceof ApiError ? error : null;
  return (
    <div role="alert" className="rounded-lg border border-destructive p-4">
      <p className="font-semibold">
        {api?.unauthenticated
          ? 'Sign in to the editor surface first'
          : api?.notFound
            ? 'This khutbah is not here'
            : 'Could not load the passages'}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {api?.unauthenticated
          ? 'The editor surface is behind a password, and a background request cannot raise the browser’s prompt by itself.'
          : error instanceof Error
            ? error.message
            : 'Something went wrong reaching the server.'}
      </p>
    </div>
  );
}

function PassageRow({
  row,
  language,
  script,
  direction,
  value,
  onChange,
  onMove,
  registerRef,
}: {
  row: Row;
  language: string;
  /** From the server's catalogue — one font per script, never derived here. */
  script: string;
  direction: 'ltr' | 'rtl';
  value: string;
  onChange: (text: string) => void;
  onMove: (delta: number) => void;
  registerRef: (element: HTMLTextAreaElement | null) => void;
}) {
  const { passage, targetIndex } = row;
  const isQuran = passage.passageType === 'quran';
  const typeLabel = PASSAGE_TYPE_LABELS[passage.passageType] ?? 'Body';

  return (
    <article className="grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-2 md:gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
          {/* One-based for a person. `ordinal` is the storage order and
              starts at zero; "Passage 0" is not something anyone says, and
              the citation anchor is the passage id, not this number. */}
          <span className="font-semibold">Passage {passage.ordinal + 1}</span>
          {passage.pageNo !== null ? <span>· p. {passage.pageNo}</span> : null}
          <span>· {typeLabel}</span>
          {isQuran ? (
            <span className="inline-flex items-center gap-1 rounded-sm bg-citation-bg px-1.5 py-0.5 font-semibold text-citation-foreground">
              Not editable
            </span>
          ) : null}
        </div>

        {passage.sourceText ? (
          <p
            lang="ar"
            dir="rtl"
            className={cn(
              'script-ar font-normal text-reading-foreground',
              isQuran && 'rounded-md border border-quote-arabic-border bg-quote-arabic-bg p-3',
            )}
          >
            {isQuran ? (
              <>
                <span className="text-citation-foreground" aria-hidden>
                  ﴿
                </span>{' '}
                {passage.sourceText}{' '}
                <span className="text-citation-foreground" aria-hidden>
                  ﴾
                </span>
              </>
            ) : (
              passage.sourceText
            )}
          </p>
        ) : (
          <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
            The Arabic for this passage could not be read from the original.
          </p>
        )}
      </div>

      {isQuran || targetIndex === null ? (
        // A row, never a field. The reader is shown the publisher's
        // translation of meaning; producing one here is forbidden outright.
        <p className="self-center rounded-md bg-muted p-3 text-sm text-muted-foreground">
          Qur’an is never translated here. The reader shows the publisher’s
          <i> translation of meaning</i>.
        </p>
      ) : (
        <label className="flex flex-col gap-1.5">
          <span className="sr-only">Translation of passage {passage.ordinal + 1}</span>
          <textarea
            ref={registerRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              // Only when the caret is at the edge, so arrow keys still move
              // within a long paragraph the way they do in any text field.
              const field = event.currentTarget;
              if (event.key === 'ArrowDown' && field.selectionStart === field.value.length) {
                event.preventDefault();
                onMove(1);
              }
              if (event.key === 'ArrowUp' && field.selectionStart === 0) {
                event.preventDefault();
                onMove(-1);
              }
            }}
            rows={3}
            dir={direction}
            lang={language}
            placeholder={`Translation of passage ${passage.ordinal + 1}`}
            className={cn(
              script,
              'min-h-20 w-full resize-y rounded-md border border-border bg-background p-3',
              'outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
            )}
          />
        </label>
      )}
    </article>
  );
}

function Footer({
  entered,
  published,
  complete,
  provenanceReady,
  filled,
  translatable,
  saveError,
  publishError,
  saving,
  publishing,
  onSave,
  onPublish,
}: {
  entered: string | null;
  published: string | null;
  complete: boolean;
  provenanceReady: boolean;
  filled: number;
  translatable: number;
  saveError: unknown;
  publishError: unknown;
  saving: boolean;
  publishing: boolean;
  onSave: () => void;
  onPublish: () => void;
}) {
  const error = saveError ?? publishError;
  const blocked = !complete
    ? `${translatable - filled} passage(s) still need a translation.`
    : !provenanceReady
      ? 'Choose who produced this translation — and name the model, if one did.'
      : null;

  return (
    <div className="sticky bottom-0 z-20 border-t border-border bg-card/95 px-4 py-3 backdrop-blur sm:px-6">
      <div className="mx-auto flex w-full max-w-[1100px] flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Something went wrong.'}
            </p>
          ) : published ? (
            <p role="status" className="text-sm">
              <span className="font-semibold">Published.</span> {published}
            </p>
          ) : entered ? (
            <p role="status" className="text-sm">
              <span className="font-semibold">Saved as a draft.</span> {entered}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Unofficial reading aid · not endorsed by the publisher.{' '}
              {/*
                True again as of V15. Publishing now sets `status` and
                nothing else: the reviewer columns are left alone, so the
                reader badges this "unreviewed" until a person approves it in
                the queue. Before V15 the schema forced a reviewer here and
                the placeholder editor was written as one.
              */}
              Publishing makes it readable and leaves it{' '}
              <i>unreviewed</i> — a reviewer approves it in the queue.
            </p>
          )}
          {blocked && !published ? (
            <p className="text-xs text-muted-foreground">{blocked}</p>
          ) : null}
        </div>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={!complete || !provenanceReady || saving}
            onClick={onSave}
          >
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            type="button"
            size="lg"
            // Publishing is only possible once something is stored: the
            // endpoint approves what exists, so offering it earlier would
            // promise something the server cannot do.
            disabled={entered === null || publishing}
            onClick={onPublish}
            title={
              entered === null
                ? 'Save the draft first'
                : 'Publishes and approves in one step — readers see it immediately.'
            }
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>
    </div>
  );
}
